import { useRef } from 'react'

import { useTruncateText } from 'src/shared/hooks/use-truncate-text'
import { cn } from 'src/shared/utils/cn'

interface TruncatedTextProps {
  text: string
  numberOfLines: number
  /**
   * A custom component to display when the text is truncated.
   */
  customOverflowComponent?: React.ReactNode
  /**
   * If true, the text will be truncated to the nearest whole word. Otherwise, it will be truncated to the nearest character.
   */
  shouldTruncateWholeWords?: boolean
  className?: string
}

/**
 * This component is used to truncate text and display a custom overflow component when the text is truncated.
 */
export default function TruncatedText({
  text,
  customOverflowComponent,
  numberOfLines = 1,
  shouldTruncateWholeWords = true,
  className,
}: TruncatedTextProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const overflowElementRef = useRef<HTMLDivElement>(null)

  const { truncatedText, truncatedTextArray } = useTruncateText({
    text,
    numberOfLines,
    shouldTruncateWholeWords,
    containerElement: containerRef,
    overflowElement: overflowElementRef,
  })

  const didTruncate = truncatedText.length !== text.length

  return (
    <div ref={containerRef} className={cn('w-full overflow-visible text-nowrap', className)}>
      {truncatedTextArray.map((textRow, index) => {
        if (index === truncatedTextArray.length - 1) {
          return null
        }
        return (
          <p key={index} className="block w-full text-nowrap text-muted">
            {textRow}
          </p>
        )
      })}
      <div className="block w-full text-nowrap text-muted">
        {truncatedTextArray[truncatedTextArray.length - 1]}
        <span ref={overflowElementRef} className="inline-block">
          {didTruncate && customOverflowComponent !== undefined && customOverflowComponent}
        </span>
      </div>
    </div>
  )
}
